import * as React from "react";
import { Link, Outlet, useLocation, useNavigate, matchPath } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sidebar } from "./Sidebar";
import { Topbar } from "./Topbar";
import { AppHeader } from "./AppHeader";
import { MobileSidebar } from "./MobileSidebar";
import { ROUTES } from "@/app/config/routes";

/**
 * MainLayout — 3 tầng shell khớp Core FE (lumina-frontend/src/app/layouts):
 *  - gradient nền hồng→xanh (lấy nguyên literal)
 *  - AppHeader full-width (brand + user session + lang)
 *  - body: Sidebar nổi trên gradient + main card trắng bo góc
 *
 * Mobile: Sidebar ẩn, hamburger ở header mở MobileSidebar drawer.
 */

const GRADIENT_BG =
  "bg-[linear-gradient(194.49deg,_rgba(255,206,218,0.31)_-3.69%,_rgba(242,233,240,0.34)_13.26%,_rgba(238,242,247,0.69)_25.42%,_rgba(230,237,246,0.71)_40.18%,_rgba(230,237,246,0.72)_52.51%,_rgba(230,237,246,0.64)_79.74%,_rgba(247,224,233,0.47)_92.63%,_rgba(255,206,218,0.3)_105.23%)]";

const APP_DETAIL_ROUTE = "/apps/:appId";

const VIEW_META: Record<string, { titleKey: string; subKey: string }> = {
  [ROUTES.upload]: { titleKey: "view.upload.title", subKey: "view.upload.sub" },
  [ROUTES.apps]: { titleKey: "view.apps.title", subKey: "view.apps.sub" },
  [APP_DETAIL_ROUTE]: { titleKey: "view.apps.title", subKey: "view.apps.sub" },
  [ROUTES.confirm]: { titleKey: "view.confirm.title", subKey: "view.confirm.sub" },
  [ROUTES.plans]: { titleKey: "view.plans.title", subKey: "view.plans.sub" },
};

function resolveMeta(pathname: string): { titleKey: string; subKey: string } {
  for (const [path, meta] of Object.entries(VIEW_META)) {
    if (path.includes(":")) {
      if (matchPath({ path, end: true }, pathname)) return meta;
    } else if (pathname === path || (path === "/" && pathname === "/")) {
      return meta;
    }
  }
  return { titleKey: "view.upload.title", subKey: "view.upload.sub" };
}

export function MainLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const meta = resolveMeta(location.pathname);
  const isAppDetail = matchPath(APP_DETAIL_ROUTE, location.pathname) !== null;
  const appPreview = (location.state as
    | {
        appPreview?: {
          app_id: string;
          name: string;
          latest_version: string | null;
          category: string | null;
        };
      }
    | undefined)?.appPreview;
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [actions, setActions] = React.useState<React.ReactNode>(null);
  const [headerMeta, setHeaderMeta] = React.useState<{
    title?: React.ReactNode;
    sub?: string;
  } | null>(null);

  React.useEffect(() => {
    if (!isAppDetail) {
      setHeaderMeta(null);
    }
  }, [isAppDetail, location.pathname]);

  const previewTitle =
    isAppDetail && appPreview ? (
      <>
        <Link to={ROUTES.apps} className="hover:text-primary underline-offset-2 hover:underline">
          {t("detail.registeredAppsRoot")}
        </Link>
        <span>{` > ${appPreview.name}`}</span>
      </>
    ) : null;
  const previewSub =
    isAppDetail && appPreview
      ? `${appPreview.name} · ${appPreview.app_id}${appPreview.latest_version ? ` · v${appPreview.latest_version}` : ""}${appPreview.category ? ` · ${appPreview.category}` : ""}`
      : null;

  return (
    <div className={`flex h-screen flex-col overflow-hidden ${GRADIENT_BG}`}>
      <AppHeader onOpenMobileMenu={() => setMobileOpen(true)} />
      <div className="flex min-h-0 flex-1 gap-2 px-2 pb-2">
        <Sidebar />
        <main className="mt-2 flex flex-1 min-w-0 flex-col overflow-hidden rounded-xl bg-white/80 transition-all duration-300 ease-in-out">
          <Topbar
            title={headerMeta?.title ?? previewTitle ?? t(meta.titleKey)}
            sub={headerMeta?.sub ?? previewSub ?? t(meta.subKey)}
            showBack={false}
            onBack={() => navigate(ROUTES.apps)}
            actions={actions}
          />
          <div className="w-full flex-1 min-h-0 overflow-auto overflow-x-hidden p-2 md:p-3 lg:p-4">
            <Outlet
              context={{
                setActions,
                setHeaderMeta,
              } satisfies MainLayoutContext}
            />
          </div>
        </main>
      </div>
      <MobileSidebar open={mobileOpen} onClose={() => setMobileOpen(false)} />
    </div>
  );
}

export type MainLayoutContext = {
  setActions: (actions: React.ReactNode) => void;
  setHeaderMeta?: (meta: { title?: React.ReactNode; sub?: string } | null) => void;
};
